import type {
  EquipmentTypeRequest,
  EquipmentTypeResponse,
  FormFieldRequest,
  FormFieldResponse,
  GetListRequestV2,
  GetListResponseOfEquipmentTypeSimpleResponse,
  GetListResponseOfSelectOption,
} from 'src/api/ProactApiClient';
import {equipmentTypeApiClient} from 'src/api/ProactApiClientInstances';
import {equipmentTypeFieldsQueryFn} from 'src/app/inspections/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Device list responses denormalize equipmentTypeName, so both tags invalidate together.
const EQUIPMENT_TYPE_TAGS = ['EquipmentTypes', 'Devices'] as const;

export const equipmentTypeApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getEquipmentTypeSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetListRequestV2
    >({
      queryFn: clientQuery(params =>
        equipmentTypeApiClient.listSelectOptions(params)
      ),
      providesTags: ['EquipmentTypes'],
    }),
    getEquipmentTypeListV2: builder.query<
      GetListResponseOfEquipmentTypeSimpleResponse,
      GetListRequestV2
    >({
      queryFn: clientQuery(params => equipmentTypeApiClient.listV2(params)),
      providesTags: ['EquipmentTypes'],
    }),
    getEquipmentType: builder.query<EquipmentTypeResponse, number>({
      queryFn: clientQuery(id => equipmentTypeApiClient.get(id)),
      providesTags: ['EquipmentTypes'],
    }),
    createEquipmentType: builder.mutation<
      EquipmentTypeResponse,
      EquipmentTypeRequest
    >({
      queryFn: clientQuery(request => equipmentTypeApiClient.create(request)),
      invalidatesTags: [...EQUIPMENT_TYPE_TAGS],
    }),
    updateEquipmentType: builder.mutation<
      EquipmentTypeResponse,
      EquipmentTypeRequest
    >({
      queryFn: clientQuery(request => equipmentTypeApiClient.update(request)),
      invalidatesTags: [...EQUIPMENT_TYPE_TAGS],
    }),
    deleteEquipmentType: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => equipmentTypeApiClient.delete(id)),
      invalidatesTags: [...EQUIPMENT_TYPE_TAGS],
    }),
    updateEquipmentTypeActiveStatus: builder.mutation<
      EquipmentTypeResponse,
      {id: number; active: boolean}
    >({
      queryFn: clientQuery(({id, active}) =>
        equipmentTypeApiClient.updateActiveStatus(id, active)
      ),
      invalidatesTags: [...EQUIPMENT_TYPE_TAGS],
    }),
    getEquipmentTypeFields: builder.query<FormFieldResponse[], number>({
      queryFn: equipmentTypeFieldsQueryFn(id =>
        equipmentTypeApiClient.getFields(id)
      ),
      providesTags: ['EquipmentTypes'],
    }),
    // FieldCount is derived from the saved field set, so a save must also
    // invalidate the list/detail tag, not just refetch fields.
    saveEquipmentTypeFields: builder.mutation<
      FormFieldResponse[],
      {id: number; fields: FormFieldRequest[]}
    >({
      queryFn: clientQuery(({id, fields}) =>
        equipmentTypeApiClient.saveFields(id, fields)
      ),
      invalidatesTags: [...EQUIPMENT_TYPE_TAGS],
    }),
  }),
});

export const {
  useGetEquipmentTypeSelectOptionsQuery,
  useGetEquipmentTypeListV2Query,
  useGetEquipmentTypeQuery,
  useCreateEquipmentTypeMutation,
  useUpdateEquipmentTypeMutation,
  useDeleteEquipmentTypeMutation,
  useUpdateEquipmentTypeActiveStatusMutation,
  useGetEquipmentTypeFieldsQuery,
  useSaveEquipmentTypeFieldsMutation,
} = equipmentTypeApi;
